import React, { FC, useEffect, useState } from 'react';
import cx from 'classnames';
import { useLocation, useParams } from 'react-router-dom';
import { News, RequestStatus } from 'types';
import { useRequest } from 'hooks';
import {
  Modal,
  Preloader,
  NoData,
  ButtonV4,
  RuTube,
  VKVideo,
} from 'components';
import {
  getMediaVideoAlbumRoute,
  URL,
} from 'appConstants';
import {
  _has_t_back,
  _t,
  _t_back,
  dateToUiFormat,
} from 'utils';

import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';

import MediaShell from '../MediaShell';
import newsStyles from '../MediaNews/styles.module.scss';
import styles from './styles.module.scss';

interface TVideos extends News {
  video: string,
  created_at: string,
}

interface VideoItem extends News {
  created_at: string,
  videos: TVideos[],
}

const PAGE_TITLES: Record<string, string> = {
  faces: 'Истории успеха',
  lifehacks: 'Лайфхаки',
  buy: 'BUY IN MOSCOW',
  experts: 'Эксперты об экспорте',
  albums: 'Видео',
};

const MediaVideos: FC = () => {
  const { id: DetailId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const url = `${URL.NEWS.VIDEO_ALBUMS.GET}`;
  const [openModalId, setOpenModalId] = useState<News | null>(null);

  const {
    data,
    request,
    status,
  } = useRequest<VideoItem[]>();

  useEffect(() => {
    request({ url });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const album = DetailId
    ? data?.find((item) => item.id === +DetailId)
    : undefined;

  const listOfData: (VideoItem | TVideos)[] = (DetailId
    ? album?.videos
    : data) || [];

  const visibleItems = listOfData.filter((el) => _has_t_back(el) && el.is_active !== false);

  const pageKey = (() => {
    if (pathname.includes('video-faces')) return 'faces';
    if (pathname.includes('video-lifehacks')) return 'lifehacks';
    if (pathname.includes('video-what-buy')) return 'buy';
    if (pathname.includes('video-about-export')) return 'experts';
    return 'albums';
  })();

  const title = album
    ? (_t_back(album, 'title') || PAGE_TITLES[pageKey])
    : PAGE_TITLES[pageKey];

  const closeModal = () => setOpenModalId(null);

  return (
    <MediaShell
      title={title || 'Видео'}
      crumbCurrent={title || 'Видео'}
    >
      <section className={newsStyles.list}>
        <Preloader isLoading={status === RequestStatus.REQUEST}>
          <NoData count={visibleItems.length || 0}>
            <ul className={newsStyles.grid}>
              {visibleItems.map((el, index) => {
                const isAlbum = !DetailId;
                const to = isAlbum
                  ? `${getMediaVideoAlbumRoute(el.id)}/${el.id}`
                  : undefined;
                const featured = index === 0;
                const withImage = Boolean(el.image) && !featured;
                const pubdate = (el as TVideos).created_at || el.pubdate || '';

                return (
                  <li
                    key={el.id}
                    className={cx(
                      newsStyles.card,
                      featured && newsStyles.cardFeatured,
                      withImage && newsStyles.cardImage,
                    )}
                    style={withImage ? {
                      ['--news-photo' as string]: `url(${el.image})`,
                    } : undefined}
                  >
                    <span className={newsStyles.badge}>
                      {_t(isAlbum ? 'АЛЬБОМ' : 'ВИДЕО')}
                    </span>
                    <div className={newsStyles.cardBody}>
                      {pubdate ? (
                        <time className={newsStyles.date} dateTime={pubdate}>
                          {dateToUiFormat(pubdate)}
                        </time>
                      ) : null}
                      <h2 className={newsStyles.cardTitle}>{_t_back(el, 'title')}</h2>
                      {isAlbum ? (
                        <ButtonV4
                          to={to}
                          label="Подробнее"
                          variant="outline"
                          tone={(featured || withImage) ? 'onDark' : 'onLight'}
                          className={newsStyles.detailBtn}
                          iconSrc={iconArrow}
                        />
                      ) : (
                        <ButtonV4
                          type="button"
                          label="Смотреть"
                          variant="outline"
                          tone={(featured || withImage) ? 'onDark' : 'onLight'}
                          className={newsStyles.detailBtn}
                          iconSrc={iconArrow}
                          onClick={() => setOpenModalId(el)}
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </NoData>
        </Preloader>
      </section>

      <Modal
        onClose={closeModal}
        isOpen={openModalId !== null}
        title={_t_back(openModalId, 'title')}
        className={styles.modal}
        classNameTitle={styles.title}
        classNameOverlay={styles.modalOverlay}
      >
        {openModalId?.video && (String(openModalId.video).indexOf('vk.com') >= 0 ? (
          <VKVideo url={String(openModalId.video)} />
        ) : (
          <RuTube url={openModalId.video} />
        ))}
      </Modal>
    </MediaShell>
  );
};

export default MediaVideos;
